import { Injectable, inject, Injector } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpEventType, HttpRequest } from '@angular/common/http';
import { Observable, throwError, from, of, firstValueFrom } from 'rxjs';
import { map, catchError, switchMap, tap, filter } from 'rxjs/operators';
import { GlobalsService } from './globals.service';
import { UserStateService } from './user-state.service';
import { AppStateService } from './app-state.service';
import { CartService, CART_PARKED_STORAGE_KEY } from './cart.service';
import { UserInfo } from '../models';

type TokenContext = 'active' | 'parked';

// Forward reference for SessionService to avoid circular dependency
let SessionServiceClass: any = null;

/**
 * Standard REST API response wrapper
 */
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

/**
 * Options for API calls
 */
export interface ApiCallOptions {
  /** Omit Authorization header (for public endpoints) */
  public?: boolean;
  /** Additional HTTP headers */
  headers?: Record<string, string>;
  /** Query parameters */
  params?: Record<string, string | number | boolean | undefined>;
  /** Response type override */
  responseType?: 'json' | 'text' | 'arraybuffer' | 'blob';
  /** Report upload progress */
  reportProgress?: boolean;
  /** Suppress session expiration handling */
  suppressSessionExpiration?: boolean;
  /** Background/polling calls: a MODULE_*_FORBIDDEN 403 must not surface the
   * soft "no access" toast (PermissionErrorInterceptor) — the user never
   * initiated the request. Callers should also handle/swallow the error. */
  suppressPermissionToast?: boolean;
}

/** Marker header consumed (and stripped) by PermissionErrorInterceptor. */
export const SKIP_PERMISSION_TOAST_HEADER = 'X-Skip-Permission-Toast';

/**
 * ApiService — Central REST API client replacing AmfService.
 *
 * - JWT Bearer token auth via UserStateService.jwtToken()
 * - X-DBP-LOCALE header from the active route/application locale
 * - Base URL from GlobalsService.apiBaseUrl
 * - 401 handling with token refresh
 * - Unwraps { success: true, data: T } response wrapper
 */
@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private readonly http = inject(HttpClient);
  private readonly globals = inject(GlobalsService);
  private readonly userState = inject(UserStateService);
  private readonly appState = inject(AppStateService);
  private readonly cartService = inject(CartService);
  private readonly injector = inject(Injector);
  private sessionService: any = null;

  /**
   * Refresh-on-401 coalesced per token context. Two slots — 'active'
   * (customer or non-impersonating admin) and 'parked' (admin's token
   * during impersonation, used for /support/* requests). They can refresh
   * concurrently (one customer-context call and one admin-context call
   * both 401-ing in flight), so a single shared promise would serialize
   * them incorrectly or refresh the wrong token.
   */
  private refreshPromises = new Map<TokenContext, Promise<boolean>>();

  /**
   * 401 codes that mean "this session is over — re-login" (customer-users
   * guide §3.6). Token refresh can't fix any of these; each gets a specific
   * dialog message instead of a silent refresh attempt.
   */
  private static readonly SESSION_ENDING_CODES: ReadonlySet<string> = new Set([
    'session_superseded',
    'sub_user_disabled',
    'account_disabled',
    'customer_users_disabled'
  ]);

  /**
   * URL prefix for endpoints that always run in admin context. While
   * impersonating, requests matching this prefix use the *parked* admin
   * JWT instead of the active customer JWT, so support-only endpoints
   * stay reachable without exiting impersonation. The match is on path
   * (not full URL), so it works for both `apiBaseUrl + /support/...` and
   * any future deployment where the base path differs.
   */
  private static readonly ADMIN_CONTEXT_PREFIX = '/support';

  // ─── Token persistence ───

  // localStorage keys. Active tokens follow the currently-acting user
  // (admin, or customer during impersonation). Parked-* keys hold the
  // admin's tokens + user object when impersonating, so a reload mid-
  // impersonation can rehydrate the banner + exit affordance.
  private static readonly LS_JWT = 'jwt';
  private static readonly LS_REFRESH = 'refreshToken';
  private static readonly LS_PARKED_JWT = 'parkedJwt';
  private static readonly LS_PARKED_REFRESH = 'parkedRefreshToken';
  private static readonly LS_PARKED_USER = 'parkedUser';

  setTokens(jwt: string, refresh: string): void {
    console.log('[ApiService] setTokens called - jwt:', jwt ? jwt.substring(0, 20) + '...' : 'NULL', 'refresh:', refresh ? refresh.substring(0, 10) + '...' : 'NULL');
    this.userState.setTokens(jwt, refresh);
    // Always persist to localStorage so tokens survive page refresh
    localStorage.setItem(ApiService.LS_JWT, jwt);
    localStorage.setItem(ApiService.LS_REFRESH, refresh);
  }

  clearTokens(): void {
    this.userState.clearTokens();
    localStorage.removeItem(ApiService.LS_JWT);
    localStorage.removeItem(ApiService.LS_REFRESH);
    // Logout drops everyone — admin and customer alike. Otherwise an admin
    // who logged out mid-impersonation would come back to a banner with
    // no live session behind it.
    localStorage.removeItem(ApiService.LS_PARKED_JWT);
    localStorage.removeItem(ApiService.LS_PARKED_REFRESH);
    localStorage.removeItem(ApiService.LS_PARKED_USER);
    // Drop the parked cart slot too — same lifecycle as the parked tokens.
    localStorage.removeItem(CART_PARKED_STORAGE_KEY);
  }

  restoreTokens(): boolean {
    const jwt = localStorage.getItem(ApiService.LS_JWT);
    if (!jwt) return false;
    // Impersonation sessions are access-token-only (the backend mints no
    // refresh token for /support/login), so an empty refresh slot must not
    // veto the restore — the access JWT alone IS the whole session there.
    const refresh = localStorage.getItem(ApiService.LS_REFRESH);
    this.userState.setTokens(jwt, refresh ?? '');
    this.restoreParkedSessionFromStorage();
    return true;
  }

  /**
   * If localStorage holds parked admin tokens + user (the page reloaded
   * mid-impersonation), feed them back into UserStateService so the
   * banner mounts and Esc/Exit can restore the admin. Treats any of the
   * three pieces missing or unparseable as corruption and clears all of
   * them — the active session keeps working as the customer.
   */
  private restoreParkedSessionFromStorage(): void {
    const parkedJwt = localStorage.getItem(ApiService.LS_PARKED_JWT);
    const parkedRefresh = localStorage.getItem(ApiService.LS_PARKED_REFRESH);
    const parkedUserRaw = localStorage.getItem(ApiService.LS_PARKED_USER);
    if (!parkedJwt && !parkedRefresh && !parkedUserRaw) return; // none — normal case
    if (!parkedJwt || !parkedRefresh || !parkedUserRaw) {
      console.warn('[ApiService] Parked session in localStorage is incomplete; clearing.');
      this.clearParkedStorage();
      return;
    }
    try {
      const parkedUser = JSON.parse(parkedUserRaw) as UserInfo;
      this.userState.restoreParkedSession(parkedUser, { access: parkedJwt, refresh: parkedRefresh });
    } catch (err) {
      console.error('[ApiService] Failed to parse parkedUser; clearing parked session.', err);
      this.clearParkedStorage();
    }
  }

  private clearParkedStorage(): void {
    localStorage.removeItem(ApiService.LS_PARKED_JWT);
    localStorage.removeItem(ApiService.LS_PARKED_REFRESH);
    localStorage.removeItem(ApiService.LS_PARKED_USER);
  }

  /**
   * Atomically swap to a customer's tokens for support impersonation.
   * Delegates to UserStateService.startSupportSession (which parks the
   * admin's tokens in memory) and mirrors the active + parked state to
   * localStorage so a refresh mid-impersonation keeps the banner and
   * the exit-impersonation affordance.
   */
  startSupportSession(targetUser: UserInfo, customerTokens: { access: string; refresh: string }): void {
    // Park the admin's cart before the user swap so the customer session
    // starts with an empty basket. Otherwise admin items leak into the
    // impersonation (and customer-session writes overwrite the admin's
    // cart on exit, since both share the same LS key).
    this.cartService.parkForSupportSession();
    this.userState.startSupportSession(targetUser, customerTokens);
    localStorage.setItem(ApiService.LS_JWT, customerTokens.access);
    localStorage.setItem(ApiService.LS_REFRESH, customerTokens.refresh);

    // Persist whatever just got parked. Reading back from userState (rather
    // than capturing the previous values up here) keeps the source of truth
    // single — userState's re-entry guard decides what's parked.
    const parkedJwt = this.userState.parkedJwtToken();
    const parkedRefresh = this.userState.parkedRefreshToken();
    const parkedUser = this.userState.originalUser();
    if (parkedJwt && parkedRefresh && parkedUser) {
      localStorage.setItem(ApiService.LS_PARKED_JWT, parkedJwt);
      localStorage.setItem(ApiService.LS_PARKED_REFRESH, parkedRefresh);
      try {
        localStorage.setItem(ApiService.LS_PARKED_USER, JSON.stringify(parkedUser));
      } catch (err) {
        console.error('[ApiService] Failed to serialize parkedUser; banner will not survive reload.', err);
      }
    }
  }

  /**
   * Restore the parked admin user + tokens. Mirrors the restored tokens
   * back to localStorage so the next reload sees the admin session, not
   * the customer's. Returns the restored user, or null if no session was
   * active.
   */
  endSupportSession(): UserInfo | null {
    const result = this.userState.endSupportSession();
    if (!result) return null;
    if (result.tokens.access) {
      localStorage.setItem(ApiService.LS_JWT, result.tokens.access);
    } else {
      localStorage.removeItem(ApiService.LS_JWT);
    }
    if (result.tokens.refresh) {
      localStorage.setItem(ApiService.LS_REFRESH, result.tokens.refresh);
    } else {
      localStorage.removeItem(ApiService.LS_REFRESH);
    }
    this.clearParkedStorage();
    // Restore the parked admin cart after the user swap. Reads from
    // localStorage, so a reload mid-impersonation followed by exit still
    // recovers the admin's original basket.
    this.cartService.restoreFromSupportSession();
    return result.user;
  }

  // ─── Core HTTP methods ───

  get<T>(path: string, options?: ApiCallOptions): Observable<T> {
    const url = this.buildUrl(path);
    const requestFn = () => this.http.get<any>(url, this.buildHttpOptions(url, options));
    return requestFn().pipe(
      map(res => this.unwrapResponse<T>(res)),
      catchError(err => this.handleError<T>(err, url, options, requestFn))
    );
  }

  post<T>(path: string, body?: any, options?: ApiCallOptions): Observable<T> {
    const blocked = this.checkSuperseded<T>();
    if (blocked) return blocked;
    const url = this.buildUrl(path);
    const requestFn = () => this.http.post<any>(url, body ?? {}, this.buildHttpOptions(url, options));
    return requestFn().pipe(
      map(res => this.unwrapResponse<T>(res)),
      catchError(err => this.handleError<T>(err, url, options, requestFn))
    );
  }

  put<T>(path: string, body?: any, options?: ApiCallOptions): Observable<T> {
    const blocked = this.checkSuperseded<T>();
    if (blocked) return blocked;
    const url = this.buildUrl(path);
    const requestFn = () => this.http.put<any>(url, body ?? {}, this.buildHttpOptions(url, options));
    return requestFn().pipe(
      map(res => this.unwrapResponse<T>(res)),
      catchError(err => this.handleError<T>(err, url, options, requestFn))
    );
  }

  patch<T>(path: string, body?: any, options?: ApiCallOptions): Observable<T> {
    const blocked = this.checkSuperseded<T>();
    if (blocked) return blocked;
    const url = this.buildUrl(path);
    const requestFn = () => this.http.patch<any>(url, body ?? {}, this.buildHttpOptions(url, options));
    return requestFn().pipe(
      map(res => this.unwrapResponse<T>(res)),
      catchError(err => this.handleError<T>(err, url, options, requestFn))
    );
  }

  delete<T>(path: string, options?: ApiCallOptions): Observable<T> {
    const blocked = this.checkSuperseded<T>();
    if (blocked) return blocked;
    const url = this.buildUrl(path);
    const requestFn = () => this.http.delete<any>(url, this.buildHttpOptions(url, options));
    return requestFn().pipe(
      map(res => this.unwrapResponse<T>(res)),
      catchError(err => this.handleError<T>(err, url, options, requestFn))
    );
  }

  /**
   * Upload a file via multipart/form-data with optional progress.
   * Does NOT set Content-Type — browser sets it automatically with boundary.
   */
  upload<T>(path: string, formData: FormData, options?: ApiCallOptions & { onProgress?: (pct: number) => void }): Observable<T> {
    const blocked = this.checkSuperseded<T>();
    if (blocked) return blocked;
    const url = this.buildUrl(path);

    const requestFn = () => {
      const req = new HttpRequest('POST', url, formData, {
        headers: this.buildAuthHeaders(url, options),
        reportProgress: true
      });
      return this.http.request(req);
    };

    return requestFn().pipe(
      tap(event => {
        if (event.type === HttpEventType.UploadProgress && event.total) {
          options?.onProgress?.(Math.round((event.loaded / event.total) * 100));
        }
      }),
      filter(event => event.type === HttpEventType.Response),
      map(event => {
        const body = (event as any).body;
        return this.unwrapResponse<T>(body);
      }),
      catchError(err => this.handleError<T>(err, url, options, requestFn as () => Observable<any>))
    );
  }

  /**
   * Chunked upload — sends file in slices via sequential POSTs.
   */
  async uploadChunked<T>(
    path: string,
    file: File,
    extraFields: Record<string, any>,
    options?: ApiCallOptions & { onProgress?: (pct: number) => void; chunkSize?: number }
  ): Promise<T | null> {
    const sessionService = this.getSessionService();
    if (sessionService?.isSessionSuperseded?.()) {
      throw new Error('Session superseded — writes blocked');
    }
    const chunkSize = options?.chunkSize ?? (2 * 1024 * 1024);
    const url = this.buildUrl(path);
    const chunks = Math.ceil(file.size / chunkSize);

    for (let index = 0; index < chunks; index++) {
      const start = index * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const buffer = await this.readSlice(file, start, end);
      const base64Chunk = this.arrayBufferToBase64(buffer);

      const payload = {
        ...extraFields,
        fileOffset: start,
        fileSize: file.size,
        chunk: base64Chunk
      };

      const raw = await this.sendChunkWithRefresh(url, payload, options);

      options?.onProgress?.(Math.round(((index + 1) / chunks) * 100));

      const trimmed = typeof raw === 'string' ? raw.trim() : '';
      // Numeric-only response means "bytes received so far" — continue
      if (/^[0-9]+$/.test(trimmed)) continue;

      // Anything else is the final response
      try {
        return JSON.parse(trimmed) as T;
      } catch {
        return trimmed as unknown as T;
      }
    }

    return null;
  }

  /**
   * Send a single chunk POST with 401/403 → token refresh → retry.
   */
  private async sendChunkWithRefresh(url: string, payload: any, options?: ApiCallOptions): Promise<string> {
    try {
      const headers = this.buildAuthHeaders(url, options);
      const raw = await firstValueFrom(this.http.post(url, payload, { headers, responseType: 'text' }));
      return typeof raw === 'string' ? raw : '';
    } catch (error: any) {
      const isUnauthorized = error?.status === 401;
      const isExpiredJwt = error?.status === 403 && error?.error?.message === 'Expired JWT Token';

      if ((isUnauthorized || isExpiredJwt) && !options?.suppressSessionExpiration) {
        const code = typeof error?.error?.error === 'string' ? error.error.error : '';
        if (ApiService.SESSION_ENDING_CODES.has(code)) {
          if (code === 'session_superseded') {
            this.notifySessionSuperseded();
          } else {
            this.notifySessionTerminated(code);
          }
          throw error;
        }

        const refreshed = await this.attemptTokenRefresh(url);
        if (refreshed) {
          try {
            const newHeaders = this.buildAuthHeaders(url, options);
            const raw = await firstValueFrom(this.http.post(url, payload, { headers: newHeaders, responseType: 'text' }));
            return typeof raw === 'string' ? raw : '';
          } catch (retryErr: any) {
            // Parity with the non-chunked retry path: a session-ending code
            // on the post-refresh retry must surface the proper dialog, not
            // leak out as a generic upload failure.
            const retryCode = typeof retryErr?.error?.error === 'string' ? retryErr.error.error : '';
            const retryAuthFailure = retryErr?.status === 401
              || (retryErr?.status === 403 && retryErr?.error?.message === 'Expired JWT Token');
            if (retryAuthFailure && !options?.suppressSessionExpiration) {
              if (retryCode === 'session_superseded') {
                this.notifySessionSuperseded();
              } else if (ApiService.SESSION_ENDING_CODES.has(retryCode)) {
                this.notifySessionTerminated(retryCode);
              } else {
                this.notifySessionExpired('JWT token expired during chunked upload retry');
              }
            }
            throw retryErr;
          }
        }

        this.notifySessionExpired('JWT token expired during chunked upload');
      }

      throw error;
    }
  }

  // ─── Public helpers (for services that need direct HttpClient calls) ───

  buildUrl(path: string): string {
    return `${this.globals.apiBaseUrl}${path}`;
  }

  /**
   * Public auth-header builder for callers using HttpClient directly. Pass
   * the request URL when possible — that lets the same impersonation-aware
   * token routing kick in for direct callers (e.g. file uploaders that
   * build their own HttpRequest). Omitting the URL falls back to the
   * active token, which is correct for non-/support endpoints.
   */
  getAuthHeaders(url?: string): HttpHeaders {
    return this.buildAuthHeaders(url, undefined);
  }

  // ─── Internal helpers ───

  /**
   * Whether a request URL targets an admin-only endpoint. Match is on
   * the path segment so it works for any apiBaseUrl. Used by both the
   * Authorization-header path and the token-refresh path.
   */
  private isAdminContextEndpoint(url: string): boolean {
    return url.includes(`${ApiService.ADMIN_CONTEXT_PREFIX}/`)
        || url.endsWith(ApiService.ADMIN_CONTEXT_PREFIX);
  }

  /**
   * Decide which token slot a request should use:
   *   - 'parked' for /support/* requests *while impersonating* (so
   *     the admin's JWT is sent, not the impersonated customer's).
   *   - 'active' otherwise.
   *
   * The "while impersonating" guard is important — a non-impersonating
   * admin hitting /support/* should still use their normal active JWT
   * (which IS the admin's). Only impersonation creates the token-mismatch
   * problem this whole machinery exists to solve.
   */
  private tokenContextForUrl(url: string): TokenContext {
    if (this.isAdminContextEndpoint(url) && this.userState.isSupportSession()) {
      return 'parked';
    }
    return 'active';
  }

  private getTokenForContext(ctx: TokenContext): string | null {
    return ctx === 'parked'
      ? this.userState.parkedJwtToken()
      : this.userState.jwtToken();
  }

  private getRefreshForContext(ctx: TokenContext): string | null {
    return ctx === 'parked'
      ? this.userState.parkedRefreshToken()
      : this.userState.refreshToken();
  }

  private buildHttpOptions(url: string, options?: ApiCallOptions): { headers: HttpHeaders; params?: HttpParams } {
    const headers = this.buildAuthHeaders(url, options);
    let params: HttpParams | undefined;

    if (options?.params) {
      params = new HttpParams();
      for (const [key, val] of Object.entries(options.params)) {
        if (val !== undefined && val !== null) {
          params = params.set(key, String(val));
        }
      }
    }

    return { headers, ...(params ? { params } : {}) };
  }

  private buildAuthHeaders(url: string | undefined, options: ApiCallOptions | undefined): HttpHeaders {
    let headers = new HttpHeaders();

    // Always include locale
    const locale = this.appState.getLocale();
    headers = headers.set('X-DBP-LOCALE', locale);

    // Add Authorization unless explicitly marked public.
    if (!options?.public) {
      // URL-aware token selection. Admin-context endpoints (/support/*)
      // hit while impersonating use the parked admin's JWT so they
      // authorize as staff; everything else uses the active token. When
      // no URL is provided (e.g. the public getAuthHeaders() helper), fall
      // back to the active token — the only honest default.
      const ctx: TokenContext = url ? this.tokenContextForUrl(url) : 'active';
      let jwt = this.getTokenForContext(ctx);
      if (ctx === 'parked' && !jwt) {
        // Shouldn't happen post-PR 1 — impersonation guarantees a parked
        // JWT — but if it does, log it loudly rather than silently sending
        // the customer JWT to /support/* (which would 401 with no useful
        // signal that the parked slot got nuked).
        console.warn('[ApiService] /support/* request during impersonation but no parked JWT; falling back to active.');
        jwt = this.userState.jwtToken();
      }
      if (jwt) {
        headers = headers.set('Authorization', `Bearer ${jwt}`);
      }
    }

    // Additional custom headers
    if (options?.headers) {
      for (const [key, val] of Object.entries(options.headers)) {
        headers = headers.set(key, val);
      }
    }

    return headers;
  }

  /**
   * Extract .data from { success: true, data: T } wrapper.
   * If the response is not a wrapper, return as-is.
   */
  private unwrapResponse<T>(res: any): T {
    if (res && typeof res === 'object' && 'success' in res && 'data' in res) {
      return res.data as T;
    }
    return res as T;
  }

  /**
   * Handle HTTP errors — 401 triggers token refresh in the matching
   * context (parked vs active). The original request URL flows through
   * so attemptTokenRefresh can pick the right refresh token.
   */
  private handleError<T>(error: any, requestUrl: string, options?: ApiCallOptions, requestFn?: () => Observable<any>): Observable<T> {
    const isUnauthorized = error?.status === 401;
    const isExpiredJwt = error?.status === 403
      && error?.error?.message === 'Expired JWT Token';

    if ((isUnauthorized || isExpiredJwt) && !options?.suppressSessionExpiration) {
      // Session-ending codes — token refresh won't fix these, user must re-login
      const errorBody = error?.error;
      const code = typeof errorBody?.error === 'string' ? errorBody.error : '';
      if (ApiService.SESSION_ENDING_CODES.has(code)) {
        if (code === 'session_superseded') {
          this.notifySessionSuperseded();
        } else {
          this.notifySessionTerminated(code);
        }
        return throwError(() => error);
      }

      return from(this.attemptTokenRefresh(requestUrl)).pipe(
        switchMap(refreshed => {
          if (!refreshed) {
            this.notifySessionExpired('JWT token expired');
            return throwError(() => error);
          }
          // Retry the original request with refreshed token
          if (requestFn) {
            return requestFn().pipe(
              map(res => this.unwrapResponse<T>(res)),
              catchError(retryErr => {
                // If the retry STILL returns a JWT-specific auth failure
                // (e.g. the refreshed token also failed validation due to a
                // multi-tab refresh race, server-side rotation glitch, or
                // clock skew), surface it as a session expiration so the
                // user sees the dialog and knows why their action didn't
                // take effect. Narrow to JWT-flagged failures only — a
                // plain 401 with a different cause (missing scope, route-
                // specific permission) should NOT force a logout.
                //
                // The message strings below are LexikJWTAuthenticationBundle
                // defaults. If the backend customizes JWT error responses,
                // update this list to match — otherwise expired tokens that
                // also fail their retry will silently leak as raw errors.
                const retryCode = typeof retryErr?.error?.error === 'string' ? retryErr.error.error : '';
                const retryExpiredJwt = retryErr?.status === 403
                  && retryErr?.error?.message === 'Expired JWT Token';
                const retrySupersededOrJwt401 = retryErr?.status === 401
                  && (ApiService.SESSION_ENDING_CODES.has(retryCode)
                      || retryErr?.error?.message === 'Expired JWT Token'
                      || retryErr?.error?.message === 'Invalid JWT Token'
                      || retryErr?.error?.message === 'JWT Token not found');
                if ((retryExpiredJwt || retrySupersededOrJwt401) && !options?.suppressSessionExpiration) {
                  if (retryCode === 'session_superseded') {
                    this.notifySessionSuperseded();
                  } else if (ApiService.SESSION_ENDING_CODES.has(retryCode)) {
                    this.notifySessionTerminated(retryCode);
                  } else {
                    this.notifySessionExpired('JWT token expired after refresh retry');
                  }
                }
                return throwError(() => retryErr);
              })
            );
          }
          return throwError(() => error);
        })
      );
    }

    return throwError(() => error);
  }

  /**
   * Refresh the token slot that matches `requestUrl` — parked admin
   * for /support/* during impersonation, active otherwise. Concurrent
   * refreshes in different contexts run in parallel; concurrent ones
   * in the same context coalesce on a single promise.
   *
   * The refresh endpoint itself (/users/token/refresh) is public — no
   * Authorization header — so it doesn't matter which client calls it.
   * What matters is which refresh-token we send as the body and which
   * slot the new tokens land in.
   */
  private async attemptTokenRefresh(requestUrl?: string): Promise<boolean> {
    const ctx: TokenContext = requestUrl ? this.tokenContextForUrl(requestUrl) : 'active';

    const existing = this.refreshPromises.get(ctx);
    if (existing) return existing;

    const refreshToken = this.getRefreshForContext(ctx);
    if (!refreshToken) return false;

    const promise = (async () => {
      try {
        const url = `${this.globals.apiBaseUrl}/users/token/refresh`;
        const res = await firstValueFrom(this.http.post<any>(url, { refresh_token: refreshToken }));
        // Handle response: tokens may be at root, in data, or in auth
        const newToken = res?.access_token || res?.token || res?.data?.access_token || res?.data?.token;
        const newRefresh = res?.refresh_token || res?.refreshToken || res?.data?.refresh_token || res?.data?.refreshToken;
        if (!newToken) return false;
        if (ctx === 'parked') {
          this.setParkedTokens(newToken, newRefresh || refreshToken);
        } else {
          this.setTokens(newToken, newRefresh || refreshToken);
        }
        return true;
      } catch {
        return false;
      } finally {
        this.refreshPromises.delete(ctx);
      }
    })();

    this.refreshPromises.set(ctx, promise);
    return promise;
  }

  /**
   * Rotate the parked admin's tokens — used by attemptTokenRefresh when
   * the failing request was admin-context. Mirrors setTokens but writes
   * to the parked signals + parked localStorage slots so the active
   * (customer) session stays untouched.
   */
  private setParkedTokens(jwt: string, refresh: string): void {
    this.userState.updateParkedTokens(jwt, refresh);
    localStorage.setItem(ApiService.LS_PARKED_JWT, jwt);
    localStorage.setItem(ApiService.LS_PARKED_REFRESH, refresh);
  }

  // ─── Session expiration (mirrors AmfService pattern) ───

  private getSessionService(): any {
    if (!this.sessionService) {
      if (!SessionServiceClass) {
        import('./session.service').then(m => {
          SessionServiceClass = m.SessionService;
          this.sessionService = this.injector.get(SessionServiceClass);
        });
        return null;
      }
      this.sessionService = this.injector.get(SessionServiceClass);
    }
    return this.sessionService;
  }

  private checkSuperseded<T>(): Observable<T> | null {
    const sessionService = this.getSessionService();
    if (sessionService?.isSessionSuperseded?.()) {
      return throwError(() => new Error('Session superseded — writes blocked'));
    }
    return null;
  }

  private notifySessionSuperseded(): void {
    const sessionService = this.getSessionService();
    if (sessionService) {
      sessionService.onSessionSuperseded();
    } else {
      import('./session.service').then(m => {
        const service = this.injector.get(m.SessionService);
        service.onSessionSuperseded();
      });
    }
  }

  private notifySessionTerminated(code: string): void {
    const sessionService = this.getSessionService();
    if (sessionService) {
      sessionService.onSessionTerminated(code);
    } else {
      import('./session.service').then(m => {
        const service = this.injector.get(m.SessionService);
        service.onSessionTerminated(code);
      });
    }
  }

  private notifySessionExpired(reason: string): void {
    const sessionService = this.getSessionService();
    if (sessionService) {
      sessionService.onSessionExpired(reason);
    } else {
      import('./session.service').then(m => {
        const service = this.injector.get(m.SessionService);
        service.onSessionExpired(reason);
      });
    }
  }

  // ─── Utility ───

  private readSlice(file: File, start: number, end: number): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as ArrayBuffer);
      reader.onerror = () => reject(new Error('Error reading file chunk'));
      reader.readAsArrayBuffer(file.slice(start, end));
    });
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    const step = 0x8000;
    for (let i = 0; i < bytes.length; i += step) {
      binary += String.fromCharCode(...bytes.subarray(i, i + step));
    }
    return btoa(binary);
  }
}
